import React, { createContext, useContext, useState, useEffect } from "react";
import { Sun, Moon } from "lucide-react";

// Theme engine for the design-system v2 token layer. A theme is a "mode" — the SAME semantic tokens
// (--vp-*) resolve to different values per [data-theme] (see tokens.css). ThemeProvider scopes the
// active theme to its own subtree (sets data-theme on a wrapper) so a region can be themed without
// touching the rest of the app — exactly what Phase 2 needs (the gallery themes itself; the 73
// existing pages stay light until Phase 5 migrates them onto these tokens).

export const THEMES = [
  { id: "light", label: "Light", hint: "Kagaz canvas — default" },
  { id: "dark", label: "Dark", hint: "Midnight-indigo — night use" },
  { id: "hc", label: "High-contrast", hint: "Pure ink/canvas — a11y" },
  { id: "sepia", label: "Sepia", hint: "Warm print — report cards" },
];

const ThemeCtx = createContext(null);
const KEY = "vp-theme-preview";

export function ThemeProvider({ children, defaultTheme = "light", className = "", style }) {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem(KEY) || defaultTheme; } catch { return defaultTheme; }
  });
  const setTheme = (t) => {
    setThemeState(t);
    try { localStorage.setItem(KEY, t); } catch { /* private mode */ }
  };
  return (
    <ThemeCtx.Provider value={{ theme, setTheme }}>
      <div data-theme={theme} className={className} style={style}>{children}</div>
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx) || { theme: "light", setTheme: () => {} };

// Subscribe to the GLOBAL theme (the resolved value on <html data-theme>) and re-render on change.
// Used by theme-adaptive assets (e.g. the PNG wordmark that must swap to a light variant in dark).
// A MutationObserver keeps it correct even when the toggle flips the theme in-page (e.g. on Login).
export function useResolvedTheme() {
  const read = () => (typeof document !== "undefined" ? document.documentElement.getAttribute("data-theme") || "light" : "light");
  const [theme, setThemeState] = useState(read);
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setThemeState(read()));
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    setThemeState(read());
    return () => obs.disconnect();
  }, []);
  return theme;
}

// ── Global theme engine (Phase 5) ───────────────────────────────────────────────────────────────
// Sets [data-theme] on <html> so migrated, token-driven surfaces re-theme app-wide. Default light, so
// not-yet-migrated (fixed-colour) pages are unaffected — dark coverage grows as screens migrate.
// The no-FOUC apply happens in public/index.html before paint; this keeps React + storage in sync.
export const GLOBAL_KEY = "vp-theme";
export const GLOBAL_THEME_EVENT = "vp:theme-change";
export const GLOBAL_THEME_PREFERENCES = Object.freeze(["system", "light", "dark", "hc", "sepia"]);
const VALID = ["light", "dark", "hc", "sepia"]; // a stale/garbage stored value can never set an unknown data-theme
const THEME_COLOR = { light: "#155EEF", dark: "#06152E", hc: "#000000", sepia: "#FFFAF0" };
export function normalizeThemePreference(pref) {
  return GLOBAL_THEME_PREFERENCES.includes(pref) ? pref : "light";
}
export function resolveTheme(pref) {
  const normalized = normalizeThemePreference(pref);
  if (normalized === "system") {
    try { return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"; } catch { return "light"; }
  }
  return VALID.includes(normalized) ? normalized : "light";
}
export function getStoredTheme() {
  try { return normalizeThemePreference(localStorage.getItem(GLOBAL_KEY) || "light"); } catch { return "light"; }
}
// Swapping the theme fires EVERY element's color/background transition simultaneously — the whole
// page "smears" for 200ms. Suppress transitions for the swap, force one reflow so the new values
// land instantly, then restore (the standard Linear/Next-themes trick).
export function withTransitionsSuppressed(mutate) {
  if (typeof document === "undefined") { mutate(); return; }
  const style = document.createElement("style");
  style.appendChild(document.createTextNode("*,*::before,*::after{transition:none!important;animation-duration:0.001ms!important}"));
  document.head.appendChild(style);
  try { mutate(); } finally {
    // eslint-disable-next-line no-unused-expressions
    window.getComputedStyle(document.body).opacity; // force reflow while suppressed
    requestAnimationFrame(() => style.remove());
  }
}

export function applyGlobalTheme(pref) {
  if (typeof document === "undefined") return;
  const r = resolveTheme(normalizeThemePreference(pref));
  withTransitionsSuppressed(() => {
    document.documentElement.setAttribute("data-theme", r);
  });
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[r] || THEME_COLOR.light); // match mobile browser chrome to the theme
}
export function setGlobalTheme(pref) {
  const normalized = normalizeThemePreference(pref);
  try { localStorage.setItem(GLOBAL_KEY, normalized); } catch { /* private mode */ }
  applyGlobalTheme(normalized);
  // The browser's `storage` event only fires in *other* tabs. A small same-document event keeps
  // every mounted toggle/select in sync when marketing pages render more than one copy (desktop
  // header, mobile drawer and footer) without introducing a global state dependency.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(GLOBAL_THEME_EVENT, { detail: { preference: normalized } }));
  }
}

// A light/dark toggle for migrated surfaces. Icon-only remains the backwards-compatible product-shell
// default; marketing chrome can opt into an explicit "Light mode" / "Dark mode" label and switch track.
// The component listens to same-tab, cross-tab and operating-system changes so the control cannot drift
// away from the actual <html data-theme> value when several instances are mounted.
export function GlobalThemeToggle({ className, showLabel = false, labelClassName = "" }) {
  const [pref, setPref] = useState(() => getStoredTheme());
  const domResolved = useResolvedTheme();
  const resolved = pref === "system" ? domResolved : resolveTheme(pref);

  useEffect(() => {
    applyGlobalTheme(pref);

    let mq;
    const onSystemTheme = () => applyGlobalTheme("system");
    if (pref === "system" && typeof window.matchMedia === "function") {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
      if (typeof mq.addEventListener === "function") mq.addEventListener("change", onSystemTheme);
      else mq.addListener?.(onSystemTheme);
    }

    return () => {
      if (typeof mq?.removeEventListener === "function") mq.removeEventListener("change", onSystemTheme);
      else mq?.removeListener?.(onSystemTheme);
    };
  }, [pref]);

  useEffect(() => {
    const sync = (next) => setPref(normalizeThemePreference(next || "light"));
    const onStorage = (event) => { if (event.key === GLOBAL_KEY) sync(event.newValue); };
    const onThemeChange = (event) => sync(event.detail?.preference);
    window.addEventListener("storage", onStorage);
    window.addEventListener(GLOBAL_THEME_EVENT, onThemeChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(GLOBAL_THEME_EVENT, onThemeChange);
    };
  }, []);

  const isDark = resolved === "dark";
  const currentLabel = ({
    light: "Light mode",
    dark: "Dark mode",
    hc: "High-contrast mode",
    sepia: "Sepia mode",
  })[resolved] || "Light mode";
  const nextLabel = isDark ? "light mode" : "dark mode";
  const toggle = () => {
    const next = isDark ? "light" : "dark";
    setPref(next);
    setGlobalTheme(next);
  };

  return (
    <button
      type="button" onClick={toggle}
      aria-label={`${currentLabel}. Switch to ${nextLabel}`} aria-pressed={isDark}
      title={`Switch to ${nextLabel}`}
      data-current-theme={resolved}
      data-testid="theme-toggle"
      className={
        "group h-11 border border-line bg-surface/85 text-ink-2 shadow-[0_1px_2px_rgb(15_42_84_/_0.06)] backdrop-blur-md " +
        "hover:border-accent/35 hover:bg-accent/5 hover:text-accent active:scale-[0.98] " +
        "transition-[color,background-color,border-color,box-shadow,transform] duration-200 " +
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/45 " +
        (showLabel
          ? "inline-flex min-w-[7.75rem] items-center justify-between gap-2.5 rounded-full py-1.5 pl-3 pr-2 "
          : "inline-grid w-11 place-items-center rounded-xl ") +
        (className || "")
      }
    >
      {showLabel ? (
        <>
          <span className={`whitespace-nowrap text-[12.5px] font-semibold tracking-[-0.01em] ${labelClassName}`} aria-hidden="true">
            {currentLabel}
          </span>
          <span className="relative h-6 w-11 shrink-0 rounded-full border border-line bg-muted shadow-inner" aria-hidden="true">
            <span
              className={
                "absolute left-0.5 top-0.5 grid h-[18px] w-[18px] place-items-center rounded-full bg-primary text-white " +
                "shadow-[0_2px_6px_rgb(29_78_216_/_0.28)] transition-transform duration-200 ease-premium " +
                (isDark ? "translate-x-[18px]" : "translate-x-0")
              }
            >
              {isDark ? <Moon className="h-3 w-3" /> : <Sun className="h-3 w-3" />}
            </span>
          </span>
        </>
      ) : isDark ? (
        <Moon className="h-[18px] w-[18px] transition-transform duration-200 group-hover:-rotate-6" aria-hidden="true" />
      ) : (
        <Sun className="h-[18px] w-[18px] transition-transform duration-200 group-hover:rotate-6" aria-hidden="true" />
      )}
    </button>
  );
}
